import {
  GroupInfo,
  IAMUserState,
  IOption,
  ISelectableOption,
  JsonSchemaItemProperty,
  LandingPageOptions,
  PrimitiveValueType,
  EnumLabels,
} from "../../../../types";
import { convertToOptions } from "../optionHelpers";
import { getGroupIdOptionsBySetup } from "../group-id/groupIdHelpers";
import { getStoreOptionsFromDetailsAPIResponse } from "../storeHelpers";
import { NO_RESULTS_DEFAULT_ISelectableOption } from "../../constants";

const getGroupIdOptionsFromDetailsAPIResponseForImport = (
  groupsInfo: GroupInfo[],
  allowedGroupIds?: string[],
): ISelectableOption[] => {
  const groupOptions = groupsInfo
    .filter(
      (group: GroupInfo) =>
        group.type === "GL_GROUP" &&
        (!allowedGroupIds || allowedGroupIds.includes(group.groupId)),
    )
    .map((group) => {
      return {
        label: group.companyId ?? group.name,
        value: group.groupId,
      };
    });

  return groupOptions.length > 0
    ? groupOptions
    : [NO_RESULTS_DEFAULT_ISelectableOption()];
};

const resolveDropdownOptionsForImport = ({
  enumValues,
  enumLabels,
  iamUserState,
  isStore,
  isGroupId,
  allowedGroupIds,
}: {
  enumValues: PrimitiveValueType[];
  enumLabels?: EnumLabels;
  iamUserState: IAMUserState;
  isStore: boolean | undefined;
  isGroupId: boolean | undefined;
  allowedGroupIds: string[];
}): ISelectableOption[] => {
  if (isGroupId) {
    return getGroupIdOptionsFromDetailsAPIResponseForImport(
      iamUserState.eisGroups.groups,
      allowedGroupIds,
    );
  }
  if (isStore) {
    return getStoreOptionsFromDetailsAPIResponse(
      iamUserState.extendedAccountInfo,
    );
  }
  return convertToOptions(enumValues, enumLabels);
};

export const getDisplayOptions = (
  property: JsonSchemaItemProperty,
  selected: LandingPageOptions,
  iamUserState: IAMUserState,
) => {
  const { isStore, isGroupId, enum: enumValues, enumLabels } = property;

  const allowedGroupIds = isGroupId
    ? getGroupIdOptionsBySetup({
        groupIds: iamUserState.eisGroups.groups,
        setup: selected.setup.value,
      }).map((option) => option.value)
    : [];

  return resolveDropdownOptionsForImport({
    enumValues: enumValues ?? [],
    enumLabels,
    isStore,
    isGroupId,
    allowedGroupIds,
    iamUserState,
  });
};

export const getSelectedGroupIdOptionForImport = (
  inputValue: string,
  groupsInfo: GroupInfo[],
): IOption => {
  if (!inputValue) return { label: "", value: "" };
  const groupInfo = groupsInfo.find(
    (groupInfo) =>
      groupInfo.groupId === inputValue ||
      groupInfo.name === inputValue ||
      groupInfo.companyId === inputValue,
  );

  return groupInfo
    ? {
        label: `${groupInfo.name}`,
        value: `${groupInfo.groupId}`,
      }
    : { label: inputValue, value: inputValue };
};
